import { useState } from 'react';
import { OnboardingStepProps, DecisionLean, MissingSignal, HiringDecision } from './types';

const MISSING_SIGNALS: MissingSignal[] = [
  { area: 'System Design', suggestion: 'Add a system design interview to evaluate architecture thinking' },
  { area: 'Collaboration', suggestion: 'Consider a cross-functional panel to assess teamwork' },
  { area: 'Coding Depth', suggestion: 'A take-home or pairing session could validate implementation skills' },
];

export function StepDecision({ state, onUpdate, onNext, onBack }: OnboardingStepProps) {
  const [lean, setLean] = useState<DecisionLean | null>(state.decision?.lean ?? null);
  const [showNextSteps, setShowNextSteps] = useState(false);

  const candidateName = state.candidate?.name || 'this candidate';
  const roleTitle = state.role?.title || 'this role';

  const handleDecision = (decision: DecisionLean) => {
    setLean(decision);
    
    const hiringDecision: HiringDecision = {
      lean: decision,
      missingSignals: decision === 'need_signal' ? MISSING_SIGNALS : undefined,
    };
    
    onUpdate({ decision: hiringDecision });
    
    if (decision === 'need_signal') {
      setShowNextSteps(true);
    } else {
      setShowNextSteps(false);
    }
  };

  const handleComplete = () => {
    onNext();
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-surface-900 mb-4">
          Based on what you see, what's your lean?
        </h1>
        <p className="text-lg text-surface-500 max-w-xl mx-auto">
          You don't need certainty. Just your current signal on {candidateName} for {roleTitle}.
        </p>
      </div>

      <div className="max-w-xl mx-auto space-y-4">
        {/* Lean Yes */}
        <button
          onClick={() => handleDecision('lean_yes')}
          className={`
            w-full p-6 rounded-2xl border-2 transition-all duration-200 text-left group
            ${lean === 'lean_yes'
              ? 'border-green-400 bg-green-50 shadow-lg shadow-green-500/10'
              : 'border-surface-200 hover:border-green-300 hover:bg-green-50/50'
            }
          `}
        >
          <div className="flex items-center gap-4">
            <div className={`
              w-14 h-14 rounded-xl flex items-center justify-center transition-colors
              ${lean === 'lean_yes' ? 'bg-green-500' : 'bg-green-100 group-hover:bg-green-200'}
            `}>
              <svg className={`w-7 h-7 ${lean === 'lean_yes' ? 'text-white' : 'text-green-600'}`} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className={`text-lg font-semibold ${lean === 'lean_yes' ? 'text-green-800' : 'text-surface-900'}`}>
                Lean yes
              </p>
              <p className="text-surface-500">
                Signal looks promising—worth moving forward.
              </p>
            </div>
            {lean === 'lean_yes' && (
              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </button>

        {/* Lean No */}
        <button
          onClick={() => handleDecision('lean_no')}
          className={`
            w-full p-6 rounded-2xl border-2 transition-all duration-200 text-left group
            ${lean === 'lean_no'
              ? 'border-red-400 bg-red-50 shadow-lg shadow-red-500/10'
              : 'border-surface-200 hover:border-red-300 hover:bg-red-50/50'
            }
          `}
        >
          <div className="flex items-center gap-4">
            <div className={`
              w-14 h-14 rounded-xl flex items-center justify-center transition-colors
              ${lean === 'lean_no' ? 'bg-red-500' : 'bg-red-100 group-hover:bg-red-200'}
            `}>
              <svg className={`w-7 h-7 ${lean === 'lean_no' ? 'text-white' : 'text-red-600'}`} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 15h2.25m8.024-9.75c.011.05.028.1.052.148.591 1.2.924 2.55.924 3.977a8.96 8.96 0 01-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 00.303-.54m.023-8.25H16.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.504c-.618 0-1.217.247-1.605.729A11.95 11.95 0 002.25 12c0 .434.023.863.068 1.285C2.427 14.306 3.346 15 4.372 15h3.126c.618 0 .991.724.725 1.282A7.471 7.471 0 007.5 19.5a2.25 2.25 0 002.25 2.25.75.75 0 00.75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 002.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384" />
              </svg>
            </div>
            <div className="flex-1">
              <p className={`text-lg font-semibold ${lean === 'lean_no' ? 'text-red-800' : 'text-surface-900'}`}>
                Lean no
              </p>
              <p className="text-surface-500">
                Signals don't align with what we're looking for.
              </p>
            </div>
            {lean === 'lean_no' && (
              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </button>

        {/* Need More Signal */}
        <button
          onClick={() => handleDecision('need_signal')}
          className={`
            w-full p-6 rounded-2xl border-2 transition-all duration-200 text-left group
            ${lean === 'need_signal'
              ? 'border-brand-400 bg-brand-50 shadow-lg shadow-brand-500/10'
              : 'border-surface-200 hover:border-brand-300 hover:bg-brand-50/50'
            }
          `}
        >
          <div className="flex items-center gap-4">
            <div className={`
              w-14 h-14 rounded-xl flex items-center justify-center transition-colors
              ${lean === 'need_signal' ? 'bg-brand-500' : 'bg-brand-100 group-hover:bg-brand-200'}
            `}>
              <svg className={`w-7 h-7 ${lean === 'need_signal' ? 'text-white' : 'text-brand-600'}`} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className={`text-lg font-semibold ${lean === 'need_signal' ? 'text-brand-800' : 'text-surface-900'}`}>
                Need more signal
              </p>
              <p className="text-surface-500">
                I know what I'm missing—let's fill the gaps.
              </p>
            </div>
            {lean === 'need_signal' && (
              <svg className="w-6 h-6 text-brand-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </button>

        {/* Missing signal suggestions */}
        {showNextSteps && lean === 'need_signal' && (
          <div className="mt-8 animate-fade-in">
            <div className="bg-brand-50 border border-brand-200 rounded-2xl p-6">
              <h3 className="font-semibold text-brand-800 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                </svg>
                Here's what signal is missing
              </h3>
              <div className="space-y-3">
                {MISSING_SIGNALS.map((signal, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-brand-100">
                    <div className="w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-brand-600">{i + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-surface-900">{signal.area}</p>
                      <p className="text-sm text-surface-600">{signal.suggestion}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-brand-700">
                Most teams add a <span className="font-medium">system design interview</span> here.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-12 flex justify-center gap-4">
        <button onClick={onBack} className="btn btn-secondary text-base px-8 py-3.5">
          Back
        </button>
        <button
          onClick={handleComplete}
          disabled={!lean}
          className={`
            btn text-base px-10 py-3.5 transition-all duration-200
            ${lean
              ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-500/25'
              : 'bg-surface-200 text-surface-400 cursor-not-allowed'
            }
          `}
        >
          Complete
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}

